import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface StaffData {
  id: string;
  first_name: string;
  last_name: string;
  status: string;
  points_total: number;
  endorsement_count?: number;
  endorsement_summary?: string | null;
  last_checked: string;
  license_valid_to: string;
  photocard_expiry?: string;
  common_entitlement_expiry: string;
  date_of_birth: string;
}

interface CheckHistoryData {
  id: string;
  check_date: string;
  first_name: string;
  last_name: string;
}

interface EndorsementSummaryItem {
  code: string;
  description: string;
}

interface EndorsementDrilldownItem {
  id: string;
  name: string;
  status: string;
  pointsTotal: number;
  lastChecked: string;
  endorsement: EndorsementSummaryItem;
}

const ENDORSEMENT_COLORS = [
  '#42A5F5',
  '#66BB6A',
  '#FFA726',
  '#AB47BC',
  '#EF5350',
  '#26C6DA',
  '#FFCA28',
  '#8D6E63',
  '#78909C',
  '#EC407A'
];

const parseEndorsementSummary = (summary?: string | null): EndorsementSummaryItem[] => {
  if (!summary) return [];

  return summary
    .split('|||')
    .map((item) => {
      const [code = '', ...descriptionParts] = item.split(':::');
      return {
        code: code.trim(),
        description: descriptionParts.join(':::').trim()
      };
    })
    .filter(item => item.code || item.description);
};

const formatEndorsementLabel = (endorsement: EndorsementSummaryItem) =>
  endorsement.description
    ? `${endorsement.code} - ${endorsement.description}`
    : endorsement.code || 'Unknown endorsement';

const DVLAAnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staffData, setStaffData] = useState<StaffData[]>([]);
  const [checkHistory, setCheckHistory] = useState<CheckHistoryData[]>([]);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');
  const [selectedEndorsement, setSelectedEndorsement] = useState<{
    label: string;
    staff: EndorsementDrilldownItem[];
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch staff data
        const staffResponse = await axios.get(`${API_BASE_URL}/dvla-staff/staff`, { headers });
        setStaffData(staffResponse.data || []);

        // Fetch check history
        const historyResponse = await axios.get(`${API_BASE_URL}/dvla-staff/check-history`, { headers });
        setCheckHistory(historyResponse.data || []);
      } catch (err: any) {
        console.error('Error fetching analytics data:', err);
        setError(err.response?.data?.error || 'Failed to fetch analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const prepareStatusData = () => {
    const statusCounts: Record<string, number> = {
      'VALID': 0,
      'EXPIRED': 0,
      'SUSPENDED': 0,
      'REVOKED': 0,
      'DISQUALIFIED': 0,
      'SURRENDERED': 0,
      'UNKNOWN': 0
    };

    staffData.forEach(staff => {
      const status = staff.status?.toUpperCase() || 'UNKNOWN';
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      } else {
        statusCounts['UNKNOWN']++;
      }
    });

    return {
      labels: Object.keys(statusCounts),
      datasets: [
        {
          data: Object.values(statusCounts),
          backgroundColor: [
            '#4CAF50', // VALID
            '#FF9800', // EXPIRED
            '#F44336', // SUSPENDED
            '#E91E63', // REVOKED
            '#9C27B0', // DISQUALIFIED
            '#795548', // SURRENDERED
            '#9E9E9E'  // UNKNOWN
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const getAgeFromDob = (dobStr: string | null | undefined): number | null => {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age >= 0 && age <= 120 ? age : null;
  };

  const prepareAgeData = () => {
    const ageBands: Record<string, number> = {
      '18–24': 0,
      '25–34': 0,
      '35–44': 0,
      '45–54': 0,
      '55–64': 0,
      '65+': 0,
      'Unknown': 0
    };

    staffData.forEach(staff => {
      const age = getAgeFromDob(staff.date_of_birth);
      if (age === null) {
        ageBands['Unknown']++;
        return;
      }
      if (age >= 18 && age <= 24) ageBands['18–24']++;
      else if (age <= 34) ageBands['25–34']++;
      else if (age <= 44) ageBands['35–44']++;
      else if (age <= 54) ageBands['45–54']++;
      else if (age <= 64) ageBands['55–64']++;
      else ageBands['65+']++;
    });

    return {
      labels: Object.keys(ageBands),
      datasets: [
        {
          data: Object.values(ageBands),
          backgroundColor: [
            '#2196F3',
            '#4CAF50',
            '#8BC34A',
            '#FFC107',
            '#FF9800',
            '#F44336',
            '#9E9E9E'
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const preparePointsData = () => {
    const pointsRanges: Record<string, number> = {
      '0 points': 0,
      '1-3 points': 0,
      '4-6 points': 0,
      '7-9 points': 0,
      '10-12 points': 0,
      '12+ points': 0
    };

    staffData.forEach(staff => {
      const points = parseInt(String(staff.points_total)) || 0;
      
      if (points === 0) pointsRanges['0 points']++;
      else if (points <= 3) pointsRanges['1-3 points']++;
      else if (points <= 6) pointsRanges['4-6 points']++;
      else if (points <= 9) pointsRanges['7-9 points']++;
      else if (points <= 12) pointsRanges['10-12 points']++;
      else pointsRanges['12+ points']++;
    });

    return {
      labels: Object.keys(pointsRanges),
      datasets: [
        {
          label: 'Number of Drivers',
          data: Object.values(pointsRanges),
          backgroundColor: [
            '#4CAF50',
            '#8BC34A',
            '#FFEB3B',
            '#FF9800',
            '#FF5722',
            '#F44336'
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const buildMonthlyExpiryDataset = (
    getDate: (staff: StaffData) => string | null | undefined,
    label: string,
    borderColor: string,
    backgroundColor: string
  ) => {
    const months: string[] = [];
    const counts: number[] = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const month = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthName = month.toLocaleString('default', { month: 'short', year: '2-digit' });
      months.push(monthName);
      counts.push(0);
    }

    staffData.forEach(staff => {
      const expiryDateStr = getDate(staff);

      if (expiryDateStr) {
        const expiryDate = new Date(expiryDateStr);

        if (!isNaN(expiryDate.getTime())) {
          const monthDiff = (expiryDate.getFullYear() - now.getFullYear()) * 12 +
                          (expiryDate.getMonth() - now.getMonth());

          if (monthDiff >= 0 && monthDiff < 12) {
            counts[monthDiff]++;
          }
        }
      }
    });
    
    return {
      labels: months,
      datasets: [
        {
          label,
          data: counts,
          borderColor,
          backgroundColor,
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  const prepareExpiryData = () =>
    buildMonthlyExpiryDataset(
      staff => staff.license_valid_to || staff.common_entitlement_expiry,
      'Licenses Expiring',
      '#2196F3',
      'rgba(33, 150, 243, 0.2)'
    );

  const preparePhotocardExpiryData = () =>
    buildMonthlyExpiryDataset(
      staff => staff.photocard_expiry,
      'Photocards Expiring',
      '#009688',
      'rgba(0, 150, 136, 0.2)'
    );

  const prepareEndorsementData = () => {
    const endorsementRanges: Record<string, number> = {
      '0 endorsements': 0,
      '1 endorsement': 0,
      '2 endorsements': 0,
      '3 endorsements': 0,
      '4+ endorsements': 0
    };

    staffData.forEach(staff => {
      const endorsements = Number(staff.endorsement_count) || 0;

      if (endorsements === 0) endorsementRanges['0 endorsements']++;
      else if (endorsements === 1) endorsementRanges['1 endorsement']++;
      else if (endorsements === 2) endorsementRanges['2 endorsements']++;
      else if (endorsements === 3) endorsementRanges['3 endorsements']++;
      else endorsementRanges['4+ endorsements']++;
    });

    return {
      labels: Object.keys(endorsementRanges),
      datasets: [
        {
          label: 'Number of Drivers',
          data: Object.values(endorsementRanges),
          backgroundColor: [
            '#4CAF50',
            '#FFC107',
            '#FF9800',
            '#FF5722',
            '#F44336'
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const endorsementBreakdown = useMemo(() => {
    const breakdown = new Map<string, { label: string; staff: EndorsementDrilldownItem[]; count: number }>();

    staffData.forEach(staff => {
      parseEndorsementSummary(staff.endorsement_summary).forEach(endorsement => {
        const key = `${endorsement.code}:::${endorsement.description}`;
        const existing = breakdown.get(key) || {
          label: formatEndorsementLabel(endorsement),
          staff: [],
          count: 0
        };

        existing.count += 1;
        existing.staff.push({
          id: staff.id,
          name: `${staff.first_name} ${staff.last_name}`.trim(),
          status: staff.status || 'Unknown',
          pointsTotal: Number(staff.points_total) || 0,
          lastChecked: staff.last_checked,
          endorsement
        });

        breakdown.set(key, existing);
      });
    });

    return Array.from(breakdown.values()).sort(
      (a, b) => b.count - a.count || a.label.localeCompare(b.label)
    );
  }, [staffData]);

  const prepareEndorsementBreakdownData = () => {
    if (endorsementBreakdown.length === 0) {
      return {
        labels: ['No endorsements'],
        datasets: [
          {
            data: [1],
            backgroundColor: ['#CFD8DC'],
            borderWidth: 1
          }
        ]
      };
    }

    return {
      labels: endorsementBreakdown.map(item => item.label),
      datasets: [
        {
          data: endorsementBreakdown.map(item => item.count),
          backgroundColor: endorsementBreakdown.map((_, index) => ENDORSEMENT_COLORS[index % ENDORSEMENT_COLORS.length]),
          borderWidth: 1
        }
      ]
    };
  };

  const prepareCheckActivityData = () => {
    const now = new Date();
    let startDate: Date;
    
    switch(timeRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'month':
      default:
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 1);
    }

    const filteredChecks = checkHistory.filter(check => {
      if (!check.check_date) return false;
      const checkDate = new Date(check.check_date);
      return checkDate >= startDate && checkDate <= now;
    });

    const checksByDate: Record<string, number> = {};
    
    for (let d = new Date(startDate); d <= now; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      checksByDate[dateKey] = 0;
    }

    filteredChecks.forEach(check => {
      if (!check.check_date) return;
      const dateKey = new Date(check.check_date).toISOString().split('T')[0];
      if (dateKey in checksByDate) {
        checksByDate[dateKey]++;
      }
    });

    const labels = Object.keys(checksByDate).map(dateKey => 
      new Date(dateKey).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    );

    return {
      labels,
      datasets: [
        {
          label: 'License Checks',
          data: Object.values(checksByDate),
          borderColor: '#673AB7',
          backgroundColor: 'rgba(103, 58, 183, 0.2)',
          fill: true,
          tension: 0.1
        }
      ]
    };
  };

  const calculateComplianceMetrics = () => {
    const totalStaff = staffData.length;
    if (totalStaff === 0) return { percentage: 0, valid: 0, total: 0 };
    
    const validLicenses = staffData.filter(staff => 
      staff.status?.toUpperCase() === 'VALID'
    ).length;
    
    return {
      percentage: Math.round((validLicenses / totalStaff) * 100),
      valid: validLicenses,
      total: totalStaff
    };
  };

  const calculateRiskMetrics = () => {
    const highRisk = staffData.filter(staff => {
      const points = parseInt(String(staff.points_total)) || 0;
      const status = staff.status?.toUpperCase();
      return points >= 7 || ['SUSPENDED', 'REVOKED', 'DISQUALIFIED'].includes(status || '');
    }).length;
    
    const mediumRisk = staffData.filter(staff => {
      const points = parseInt(String(staff.points_total)) || 0;
      const status = staff.status?.toUpperCase();
      return (points >= 4 && points < 7) || status === 'EXPIRED';
    }).length;
    
    const lowRisk = staffData.length - highRisk - mediumRisk;
    
    return { highRisk, mediumRisk, lowRisk };
  };

  const endorsementBreakdownChartOptions: any = {
    maintainAspectRatio: false,
    onClick: (_event: unknown, elements: Array<{ index: number }>) => {
      if (!elements?.length || endorsementBreakdown.length === 0) return;
      const selectedSlice = endorsementBreakdown[elements[0].index];
      if (selectedSlice) {
        setSelectedEndorsement({
          label: selectedSlice.label,
          staff: selectedSlice.staff
        });
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const complianceMetrics = calculateComplianceMetrics();
  const riskMetrics = calculateRiskMetrics();
  const checkMetrics = {
    total: checkHistory.length,
    lastMonth: checkHistory.filter(c => {
      const checkDate = new Date(c.check_date);
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      return checkDate >= lastMonth;
    }).length
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Driver License Analytics Dashboard</Typography>
      
      {/* Summary Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Compliance Rate</Typography>
              <Typography variant="h3" color="primary">{complianceMetrics.percentage}%</Typography>
              <Typography variant="body2" color="text.secondary">
                {complianceMetrics.valid} of {complianceMetrics.total} drivers compliant
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Risk Assessment</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Box>
                  <Typography variant="body2" color="error">High</Typography>
                  <Typography variant="h4">{riskMetrics.highRisk}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="warning">Medium</Typography>
                  <Typography variant="h4">{riskMetrics.mediumRisk}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="success">Low</Typography>
                  <Typography variant="h4">{riskMetrics.lowRisk}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>License Checks</Typography>
              <Typography variant="h3" color="primary">{checkMetrics.total}</Typography>
              <Typography variant="body2" color="text.secondary">
                {checkMetrics.lastMonth} in the last 30 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>License Status Distribution</Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Pie data={prepareStatusData()} options={{ maintainAspectRatio: false }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Points Distribution</Typography>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={preparePointsData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Drivers'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Age Distribution</Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Pie data={prepareAgeData()} options={{ maintainAspectRatio: false }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>License Expiry Timeline</Typography>
              <Box sx={{ height: 300 }}>
                <Line 
                  data={prepareExpiryData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Licenses'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Photocard Expiry Timeline</Typography>
              <Box sx={{ height: 300 }}>
                <Line
                  data={preparePhotocardExpiryData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Photocards'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Endorsement Breakdown</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click a segment to see affected staff.
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Pie data={prepareEndorsementBreakdownData()} options={endorsementBreakdownChartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Endorsements Distribution</Typography>
              <Box sx={{ height: 300 }}>
                <Bar
                  data={prepareEndorsementData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Drivers'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Check Activity</Typography>
              <ToggleButtonGroup
                value={timeRange}
                exclusive
                onChange={(_, newValue) => newValue && setTimeRange(newValue)}
                size="small"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ height: 280 }}>
                <Line 
                  data={prepareCheckActivityData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Number of Checks'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={!!selectedEndorsement}
        onClose={() => setSelectedEndorsement(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedEndorsement?.label || 'Endorsement details'}</DialogTitle>
        <DialogContent>
          {selectedEndorsement && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedEndorsement.staff.length} matching endorsement{selectedEndorsement.staff.length === 1 ? '' : 's'} found.
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Driver</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Last Checked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedEndorsement.staff.map((staff, index) => (
                      <TableRow key={`${staff.id}-${staff.endorsement.code}-${index}`}>
                        <TableCell>{staff.name || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip label={staff.status} size="small" />
                        </TableCell>
                        <TableCell>{staff.pointsTotal}</TableCell>
                        <TableCell>{staff.endorsement.description || staff.endorsement.code}</TableCell>
                        <TableCell>
                          {staff.lastChecked ? new Date(staff.lastChecked).toLocaleDateString('en-GB') : 'Never'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEndorsement(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DVLAAnalyticsDashboard;

